/**
 * Switches which organisation the current session acts as.
 *
 * A deliberate port of the accounts app's OrgSwitcher — same membership list,
 * so it reads identically in both apps.
 * Renders nothing without an organisation, and a plain (non-clickable) label
 * with exactly one: a control that only ever has one option is a lie about what
 * the product can do.
 *
 * The active organisation is server-side state (`vcs_developers_active_org`,
 * keyed by the bearer token's `sub`), not client state. Switching is a POST
 * followed by a full reload so data fetched under the previous org can't linger.
 *
 * NOTE (developer-specific): switching persists the choice, but it does NOT yet
 * change what you see. Developer reads are user-scoped, not org-scoped:
 * `vcs_developers_user_devices` has no organisation column at all, and
 * `vcs_developers_telemetry_logs` stamps `org_id` on insert while every select
 * filters on `user_id` alone. Until those reads gate on the active org, this
 * control is a preference, not a data filter. See vecros-api
 * `docs/org-scoping-plan.md`.
 */
import { useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";
import { useSnackbar } from "notistack";
import { setActiveOrganisation } from "./newBackendApi/organisations";
import { useMemberships } from "../hooks/useOrganisations";
import { ROUTES } from "../routes";

// The theme's primary.main is #000000 (a surface colour), not an accent; use the
// app's hardcoded accent blue, matching the sidebar and navbar.
const ACCENT = "#3a8eff";

// Sections whose sub-paths address one organisation's records. Reloading such a
// page after a switch would re-request another org's row, so land on the owning
// list instead. When a section starts gating on the workspace, add it here.
//
// Propulsion is deliberately absent. Its two halves are scoped differently and
// only one is a record: `vcs_developers_propulsion_comparisons` is
// workspace-owned, but it has no route of its own (the compare page reads
// `?comparison=`), while `/propulsion-calculator/<id>` opens a *combination* —
// a shared reference catalogue row with no owner column at all, identical in
// every workspace. Bouncing off it would kick people out of a page that works.
const RECORD_ROUTES = [ROUTES.devices, ROUTES.logAnalyzer, ROUTES.protectedDownloads];

export const listRouteFor = (pathname) => {
  const path = pathname.replace(/\/+$/, "") || "/";
  return RECORD_ROUTES.find((route) => path.startsWith(`${route}/`)) || null;
};

const OrgSwitcher = () => {
  const theme = useTheme();
  const [anchor, setAnchor] = useState(null);
  const [switching, setSwitching] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  // Cached membership list (GET /developer/organisations), same rows as the
  // accounts switcher. On error (not signed in / no orgs) data is undefined
  // and the switcher renders nothing.
  const { data: membershipData } = useMemberships();
  const organisations = membershipData?.organisations || [];
  const activeOrgId = membershipData?.active_organisation_id ?? null;

  if (organisations.length === 0) return null;

  const activeOrg = organisations.find((org) => org.organisation_id === activeOrgId) || null;
  // Don't fall back to organisations[0] when nothing is active — that renders a
  // workspace name the user is not actually in, which is worse than saying so.
  const label = activeOrg?.name || activeOrgId || "Select organisation";

  if (organisations.length === 1) {
    return (
      <Typography
        sx={{ fontSize: 13, fontWeight: 600, color: theme.palette.text.color, px: 1 }}
        title="You belong to one organisation"
      >
        {label}
      </Typography>
    );
  }

  const choose = async (organisationId) => {
    if (organisationId === activeOrgId) {
      setAnchor(null);
      return;
    }
    setSwitching(organisationId);
    try {
      await setActiveOrganisation(organisationId);
      // Reload so nothing fetched under the previous org lingers on screen.
      const landing = listRouteFor(window.location.pathname);
      if (landing) window.location.assign(landing);
      else window.location.reload();
    } catch (error) {
      setSwitching(null);
      setAnchor(null);
      enqueueSnackbar(
        error?.response?.status === 403
          ? "You are no longer a member of that organisation."
          : "Couldn't switch organisation. Please try again.",
        { variant: "error" }
      );
    }
  };

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        aria-label="Switch organisation"
        onClick={(e) => setAnchor(e.currentTarget)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setAnchor(e.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.25,
          px: 1,
          py: 0.5,
          borderRadius: "8px",
          cursor: "pointer",
          color: theme.palette.text.color,
          "&:hover": { backgroundColor: theme.palette.tertiary.main },
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{label}</Typography>
        <ArrowDropDownIcon
          sx={{ fontSize: 20, transform: anchor ? "rotate(180deg)" : "none", transition: "0.2s" }}
        />
      </Box>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { width: 260 } } }}
      >
        <Typography
          sx={{
            px: 2,
            py: 1,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: theme.palette.secondaryText.color,
          }}
        >
          Organisations
        </Typography>
        {organisations.map((org) => {
          const active = org.organisation_id === activeOrgId;
          return (
            <MenuItem
              key={org.organisation_id}
              selected={active}
              disabled={Boolean(switching)}
              onClick={() => choose(org.organisation_id)}
            >
              <ListItemText
                primary={org.name}
                secondary={org.role}
                primaryTypographyProps={{ fontSize: 13.5 }}
                secondaryTypographyProps={{ fontSize: 11, textTransform: "capitalize" }}
              />
              {switching === org.organisation_id ? (
                <CircularProgress size={16} sx={{ color: ACCENT }} />
              ) : (
                active && <CheckIcon sx={{ fontSize: 18, color: ACCENT }} />
              )}
            </MenuItem>
          );
        })}
        <Box sx={{ px: 2, py: 1 }}>
          <Chip
            size="small"
            label="Switching reloads the app"
            sx={{ fontSize: 10.5, color: theme.palette.secondaryText.color }}
          />
        </Box>
      </Menu>
    </>
  );
};

export default OrgSwitcher;
